import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { cropPhase, pondAreaM2, type Crop, type PondFacts } from '@/lib/crop';

import { Chip } from './shared';
import { headerActionClass } from './format';

interface Sibling {
  id: string;
  name: string;
}

interface PondPageHeaderProps {
  pond: PondFacts;
  crop: Crop | null;
  prev?: Sibling | null;
  next?: Sibling | null;
  cropPicker?: ReactNode;
  actions?: ReactNode;
}

const fmt = (d: string) => format(parseISO(d), 'MMM d');

function cropChip(crop: Crop) {
  const phase = cropPhase(crop);
  if (phase === 'prep') {
    return <Chip tone="prep">Preparing{crop.prep_day ? ` · Prep day ${crop.prep_day}` : ''}</Chip>;
  }
  if (phase === 'grow_out') {
    return <Chip tone="ok">Active{crop.doc ? ` · DOC ${crop.doc}` : ''}</Chip>;
  }
  return <Chip tone="neutral"><span className="capitalize">{crop.status}</span></Chip>;
}

function pondFacts(pond: PondFacts, crop: Crop | null): string[] {
  const facts: string[] = [];
  const m2 = pondAreaM2(pond.area_ha);
  if (m2) facts.push(`${Math.round(m2).toLocaleString()} m²`);
  if (pond.pond_type) facts.push(pond.pond_type);
  if (!crop) return facts;
  if (crop.stocking_date) {
    facts.push(`stocked ${format(parseISO(crop.stocking_date), 'MMM d, yyyy')}`);
  } else if (crop.prep_start_date) {
    facts.push(`water in ${fmt(crop.prep_start_date)}`);
    if (crop.planned_stocking_date) facts.push(`planned stocking ${fmt(crop.planned_stocking_date)}`);
  }
  if (cropPhase(crop) === 'closed' && crop.actual_harvest_date) {
    facts.push(`ended ${format(parseISO(crop.actual_harvest_date), 'MMM d, yyyy')}`);
  }
  return facts;
}

export function PondPageHeader({ pond, crop, prev, next, cropPicker, actions }: PondPageHeaderProps) {
  const alerts = pond.alert_count ?? 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
          <Link to="/ponds" className="font-medium text-primary-600 hover:text-primary-700">
            Ponds
          </Link>
          <span aria-hidden="true">/</span>
          <span className="truncate text-gray-700">{pond.name}</span>
        </nav>
        <div className="flex gap-2">
          <SiblingLink sibling={prev} dir="prev" />
          <SiblingLink sibling={next} dir="next" />
        </div>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{pond.name}</h1>
            {crop ? cropChip(crop) : <Chip tone="neutral"><span className="capitalize">{pond.status}</span></Chip>}
            {crop?.cycle_number ? <Chip tone="neutral">Crop {crop.cycle_number}</Chip> : null}
            {alerts > 0 && (
              <Chip tone="crit">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                {alerts} {alerts === 1 ? 'alert' : 'alerts'}
              </Chip>
            )}
          </div>
          <p className="text-sm text-gray-600">{pondFacts(pond, crop).join(' · ')}</p>
          {cropPicker}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </header>
    </div>
  );
}

function SiblingLink({ sibling, dir }: { sibling?: Sibling | null; dir: 'prev' | 'next' }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  const cls =
    'inline-flex min-h-[40px] items-center gap-1 rounded-lg border px-3 text-sm font-semibold';
  if (!sibling) {
    return (
      <span aria-hidden="true" className={cn(cls, 'border-gray-100 text-gray-300')}>
        <Icon className="h-4 w-4" />
      </span>
    );
  }
  return (
    <Link
      to="/ponds/$pondId"
      params={{ pondId: sibling.id }}
      search={(prev) => ({ tab: prev.tab })}
      aria-label={`${dir === 'prev' ? 'Previous' : 'Next'} pond: ${sibling.name}`}
      className={cn(cls, 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50')}
    >
      {dir === 'prev' && <Icon className="h-4 w-4" />}
      <span className="max-w-[7rem] truncate">{sibling.name}</span>
      {dir === 'next' && <Icon className="h-4 w-4" />}
    </Link>
  );
}

export function HeaderAction({
  children,
  primary,
  ...rest
}: { children: ReactNode; primary?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={headerActionClass(primary)} {...rest}>
      {children}
    </button>
  );
}
